// PurpleDoorIDS.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  motion,
  AnimatePresence,
  useInView,
  useMotionValue,
  useSpring,
  MotionConfig,
} from "motion/react";
import PurpleDoorIDSTable from "./utils/PurpleDoorIDSTable";

/* ───────────────────────── Animation Variants ───────────────────────── */
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  },
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.06, delayChildren: 0.08 },
  },
};

const staggerItem = {
  hidden: { opacity: 0, y: 18 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 120, damping: 18 },
  },
};

/* ───────────────────────── Animated Counter ───────────────────────── */
function Counter({ value, suffix = "", decimals = 0 }) {
  const ref = useRef(null);
  const motionValue = useMotionValue(0);
  const spring = useSpring(motionValue, { stiffness: 80, damping: 22 });
  const isInView = useInView(ref, { once: true, margin: "-40px" });

  useEffect(() => {
    if (isInView) motionValue.set(value);
  }, [isInView, value, motionValue]);

  useEffect(() => {
    return spring.on("change", (v) => {
      if (ref.current) {
        const formatted =
          decimals > 0
            ? v.toFixed(decimals)
            : Math.floor(v).toLocaleString("en-IN");
        ref.current.textContent = formatted + suffix;
      }
    });
  }, [spring, suffix, decimals]);

  return (
    <span ref={ref} className="tabular-nums">
      0
    </span>
  );
}

/* ───────────────────────── Data ───────────────────────── */
const whyCards = [
  {
    icon: "🎯",
    title: "Purpose",
    desc: "Create content that solves real problems and improves lives.",
  },
  {
    icon: "🏅",
    title: "Recognition",
    desc: "Get discovered through curated playlists and community love.",
  },
  {
    icon: "💰",
    title: "Income",
    desc: "Multiple streams beyond ads — memberships, products, consulting.",
  },
  {
    icon: "📜",
    title: "Digital Legacy",
    desc: "Evergreen knowledge that keeps helping long after you publish.",
  },
  {
    icon: "🤝",
    title: "Community",
    desc: "Creators, mentors & learners who value depth over virality.",
  },
  {
    icon: "🇮🇳",
    title: "Nation Building",
    desc: "Skills, livelihoods and empowerment across communities.",
  },
];

const earnings = [
  {
    stage: "Beginner",
    journey: "Learning & first uploads",
    range: "₹0 – ₹500",
    badge: "slate",
  },
  {
    stage: "Early Creator",
    journey: "Regular uploads",
    range: "₹500 – ₹5,000",
    badge: "slate",
  },
  {
    stage: "Monetized Creator",
    journey: "YouTube Partner Program",
    range: "₹5,000 – ₹25,000",
    badge: "teal",
  },
  {
    stage: "Growing Creator",
    journey: "Strong community",
    range: "₹25,000 – ₹1,00,000",
    badge: "teal",
  },
  {
    stage: "Professional Creator",
    journey: "Multiple revenue streams",
    range: "₹1,00,000+",
    badge: "amber",
  },
];

const revenueSources = [
  {
    source: "Ad Revenue",
    desc: "YouTube Partner Program",
    potential: "Medium",
    example: "₹2–15 / 1K views",
  },
  {
    source: "Channel Membership",
    desc: "Monthly paid community",
    potential: "High",
    example: "₹49–499 / member",
  },
  {
    source: "Super Thanks / Chat",
    desc: "Viewer tips",
    potential: "Variable",
    example: "₹10–5,000",
  },
  {
    source: "Affiliate Marketing",
    desc: "Recommended tools",
    potential: "High",
    example: "10–30% commission",
  },
  {
    source: "Brand Collaborations",
    desc: "Sponsored meaningful content",
    potential: "High",
    example: "₹10K–2L+",
  },
  {
    source: "Digital Products",
    desc: "Templates, guides, toolkits",
    potential: "Very High",
    example: "₹199–4,999",
  },
  {
    source: "Consulting / Coaching",
    desc: "1:1 or group sessions",
    potential: "Very High",
    example: "₹1,500–15,000/hr",
  },
  {
    source: "Courses",
    desc: "Structured learning",
    potential: "Very High",
    example: "₹999–15,000",
  },
  {
    source: "Community Support",
    desc: "Direct contributions",
    potential: "Medium",
    example: "₹100–2,000",
  },
];

const categories = [
  {
    icon: "👩‍💼",
    title: "Women Empowerment",
    desc: "Skills, confidence & economic independence",
    ideas: "Home business, financial freedom, leadership",
  },
  {
    icon: "🎓",
    title: "Youth Employment",
    desc: "Career readiness & job-ready skills",
    ideas: "Resume, interviews, freelancing",
  },
  {
    icon: "🏪",
    title: "MSME Growth",
    desc: "Practical growth for small businesses",
    ideas: "Digital marketing, GST, systems",
  },
  {
    icon: "🤖",
    title: "AI Adoption",
    desc: "Everyday AI for non-tech users",
    ideas: "ChatGPT for teachers, AI for shops",
  },
  {
    icon: "🚀",
    title: "Entrepreneurship",
    desc: "From idea to first customers",
    ideas: "Lean startup, validation, sales",
  },
  {
    icon: "📊",
    title: "Financial Literacy",
    desc: "Money skills for real life",
    ideas: "Budgeting, SIPs, emergency funds",
  },
  {
    icon: "🧭",
    title: "Leadership",
    desc: "Influence without authority",
    ideas: "Team habits, feedback, decisions",
  },
  {
    icon: "⚡",
    title: "Productivity",
    desc: "Systems that free time",
    ideas: "Deep work, Notion, reviews",
  },
  {
    icon: "🏘️",
    title: "Community Development",
    desc: "Local change that scales",
    ideas: "Skill circles, mentorship",
  },
  {
    icon: "🇮🇳",
    title: "Nation Building",
    desc: "Knowledge that strengthens society",
    ideas: "Civic skills, digital public goods",
  },
  {
    icon: "💻",
    title: "Digital Skills",
    desc: "Practical tools for the digital age",
    ideas: "Canva, Workspace, coding basics",
  },
  {
    icon: "💡",
    title: "Innovation",
    desc: "Creative problem-solving",
    ideas: "Design thinking, frugal innovation",
  },
];

const creators = [
  {
    initials: "AP",
    name: "Ananya Patel",
    focus: "Women Empowerment",
    lang: "Hindi",
    subs: "42.3K",
    videos: 87,
    views: "8.2K",
    rating: "4.9",
  },
  {
    initials: "RK",
    name: "Rahul Krishnan",
    focus: "Youth Employment",
    lang: "English",
    subs: "28.1K",
    videos: 64,
    views: "6.5K",
    rating: "4.8",
  },
  {
    initials: "SM",
    name: "Sunita Mehta",
    focus: "MSME Growth",
    lang: "Gujarati",
    subs: "19.7K",
    videos: 112,
    views: "4.1K",
    rating: "4.7",
  },
  {
    initials: "VK",
    name: "Vikram Rao",
    focus: "AI Adoption",
    lang: "English + Telugu",
    subs: "61.4K",
    videos: 53,
    views: "12.8K",
    rating: "4.9",
  },
  {
    initials: "PJ",
    name: "Priya Joseph",
    focus: "Financial Literacy",
    lang: "Malayalam",
    subs: "15.2K",
    videos: 76,
    views: "3.9K",
    rating: "4.8",
  },
  {
    initials: "AS",
    name: "Arjun Singh",
    focus: "Entrepreneurship",
    lang: "Hindi",
    subs: "33.8K",
    videos: 91,
    views: "7.4K",
    rating: "4.7",
  },
  {
    initials: "NB",
    name: "Neha Bansal",
    focus: "Digital Skills",
    lang: "Hindi + English",
    subs: "47.6K",
    videos: 128,
    views: "9.1K",
    rating: "4.9",
  },
  {
    initials: "MD",
    name: "Mohammed Danish",
    focus: "Productivity",
    lang: "Urdu + English",
    subs: "22.9K",
    videos: 58,
    views: "5.3K",
    rating: "4.6",
  },
  {
    initials: "LT",
    name: "Lakshmi Thapa",
    focus: "Community Development",
    lang: "Nepali + Hindi",
    subs: "11.4K",
    videos: 49,
    views: "2.8K",
    rating: "4.8",
  },
  {
    initials: "CG",
    name: "Carlos Gomes",
    focus: "Leadership",
    lang: "Portuguese + English",
    subs: "38.2K",
    videos: 72,
    views: "8.7K",
    rating: "4.9",
  },
];

const journey = [
  "Register",
  "Learn",
  "Create",
  "Publish on YouTube",
  "IDS Playlist",
  "Discoverability",
  "Subscribers",
  "Watch Time",
  "Income",
  "Recognition",
  "Mentor",
];

const faqs = [
  {
    q: "Do I still own my YouTube channel?",
    a: "Yes. 100%. You create, publish and own your channel. The company (Think5Whys Global Deeptech Solutions Private Limited) only discovers, organizes and promotes your videos through thematic playlists.",
  },
  {
    q: "Who owns the copyright of my videos?",
    a: "You do. The company never claims ownership of your content. We only help surface it to the right audiences.",
  },
  {
    q: "How do I actually earn money?",
    a: "Through YouTube Partner Program, memberships, Super Thanks, affiliate links, brand collaborations, digital products, consulting and courses.",
  },
  {
    q: "Do I need to be a professional creator already?",
    a: "No. IDS is designed for beginners, homemakers, teachers, professionals and MSME owners. Guidance is provided so you can start even if you’ve never uploaded before.",
  },
  {
    q: "Can I create in my local language?",
    a: "Absolutely. We actively encourage content in Hindi, regional Indian languages and other global languages.",
  },
  {
    q: "Is AI usage allowed?",
    a: "Yes — responsibly. Use AI as a co-pilot for research, scripting or editing. Your unique experience and voice must remain the core.",
  },
  {
    q: "Who is eligible to join?",
    a: "Anyone 16+ who wants to create transformation-oriented content. Special focus on youth, women, teachers, professionals, coaches and MSME owners.",
  },
  {
    q: "What support will I receive?",
    a: "Deafult consent to use Think5Whys modules, features, philosophies etc, optional mentoring pathways by Digital Boutique Advisors, and visibility support.",
  },
];

/* ───────────────────────── Component ───────────────────────── */
export default function PurpleDoorIDS() {
  const [openFaq, setOpenFaq] = useState(null);
  const [search, setSearch] = useState("");

  const filteredCreators = creators.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.focus.toLowerCase().includes(search.toLowerCase()) ||
      c.lang.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <MotionConfig reducedMotion="user">
      <div className="min-h-screen bg-zinc-950 text-zinc-100 font-sans antialiased">
        {/* ───── NAV ───── */}
        <nav className="sticky top-0 z-50 border-b border-zinc-800/80 bg-zinc-950/80 backdrop-blur-xl">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 md:px-6">
            <a
              href="#"
              className="text-base font-semibold tracking-tight md:text-lg"
            >
              Think5Whys <span className="text-teal-400">· IDS</span>
            </a>
            <div className="hidden items-center gap-6 text-sm text-zinc-200 md:flex">
              <a href="#why" className="hover:text-white transition">
                Why IDS
              </a>
              <a href="#earn" className="hover:text-white transition">
                Earnings
              </a>
              <a href="#create" className="hover:text-white transition">
                Create
              </a>
              <a href="#creators" className="hover:text-white transition">
                Creators
              </a>
              <a href="#impact" className="hover:text-white transition">
                Impact
              </a>
            </div>
            <motion.a
              href="#join"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="rounded-full bg-teal-600 px-3.5 py-1.5 text-xs font-semibold text-white md:px-5 md:py-2 md:text-sm"
            >
              Create & Link
            </motion.a>
          </div>
        </nav>

        {/* ───── 1. HERO ───── */}
        <header className="relative overflow-hidden px-4 pb-16 pt-12 md:px-6 md:pb-24 md:pt-20">
          <div className="pointer-events-none absolute -right-20 -top-20 h-96 w-96 rounded-full bg-teal-500/10 blur-3xl" />
          <div className="mx-auto grid max-w-6xl gap-10 md:grid-cols-[1.15fr_0.85fr] md:items-left">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
              <motion.div
                variants={staggerItem}
                className="mb-4 inline-flex items-left gap-1.5 rounded-full bg-amber-500/10 px-3 py-1 text-xs font-medium text-amber-300"
              >
                🌍 A global movement for meaningful creators
              </motion.div>
              <motion.h1
                variants={staggerItem}
                className="text-xl lg:text-2xl font-bold leading-tight tracking-tight md:text-2xl"
              >
                Inclusive Digital
                <span className="text-teal-400">Sustenance</span> (IDS)
              </motion.h1>
              <motion.p
                variants={staggerItem}
                className="mt-2 text-sm text-zinc-200 md:text-lg"
              >
                Meaningful & Longevity-Based Digital Content Creation &
                Monetization
              </motion.p>
              <motion.ul
                variants={staggerItem}
                className="mt-3 space-y-2 text-sm text-zinc-200 md:text-base"
              >
                {[
                  "Explore Think5Whys Platform",
                  "Record User Videos in Own Language & Style",
                  "Create meaningful YouTube videos",
                  "Build your Digital Legacy",
                  "Earn sustainable income",
                  "Help transform lives",
                ].map((t) => (
                  <li key={t} className="flex items-center gap-2 text-gray-100">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-teal-600 text-[10px] text-white">
                      ✓
                    </span>
                    {t}
                  </li>
                ))}
              </motion.ul>
              <motion.div
                variants={staggerItem}
                className="mt-4 flex flex-wrap gap-2.5"
              >
                <motion.a
                  href="#join"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="rounded-full bg-teal-600 px-4 py-2 text-xs font-semibold hover:text-yellow-200 text-white shadow-lg shadow-teal-900/40 md:px-6 md:py-2.5 md:text-sm"
                >
                  🚀 Become a Transformation Creator
                </motion.a>
                <a
                  href="https://www.youtube.com/@think5whys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full bg-teal-600 px-4 py-2 text-xs font-semibold hover:text-yellow-200 text-white shadow-lg shadow-teal-900/40 md:px-6 md:py-2.5 md:text-sm"
                  // className="rounded-full border border-zinc-700 px-4 py-2 text-xs font-medium text-zinc-200 hover:border-teal-500 hover:text-teal-300 md:px-5 md:py-2.5 md:text-sm"
                >
                  ▶️ Watch Demo
                </a>
                <a
                  href="https://creatoracademy.youtube.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full bg-teal-600 px-4 py-2 text-xs font-semibold hover:text-yellow-200 text-white shadow-lg shadow-teal-900/40 md:px-6 md:py-2.5 md:text-sm"
                  // className="rounded-full px-3 py-2 text-xs font-medium text-teal-400 hover:bg-teal-500/10 md:text-sm"
                >
                  📘 Creator Guide
                </a>
                <motion.a
                  href="#visionSimulation"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="rounded-full bg-teal-600 px-4 py-2 text-xs font-semibold hover:text-yellow-200 text-white shadow-lg shadow-teal-900/40 md:px-6 md:py-2.5 md:text-sm"
                  // className="rounded-full bg-teal-600 px-4 py-2 text-xs font-semibold text-white shadow-lg shadow-teal-900/40 md:px-6 md:py-2.5 md:text-sm"
                >
                  🚀 Growth Vision Simulation
                </motion.a>

              </motion.div>
            </motion.div>

            {/* Mini Impact Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5 shadow-xl backdrop-blur"
            >
              <p className="mb-4 text-sm lg:text-lg font-medium text-zinc-200">
                Community Snapshot (Vision Simulation)
              </p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { v: 9840, l: "Creators" },
                  { v: 12600, l: "Videos" },
                  { v: 48, l: "Languages" },
                  { v: 11.8, l: "M+ Views", d: 1 },
                ].map((s) => (
                  <div
                    key={s.l}
                    className="rounded-xl bg-zinc-800/60 p-3 text-center"
                  >
                    <div className="text-xl font-bold text-teal-400 md:text-2xl">
                      <Counter value={s.v} decimals={s.d || 0} />
                    </div>
                    <div className="text-[10px] text-zinc-200 md:text-xs">
                      {s.l}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </header>

        {/* ───── 2. WHY IDS ───── */}
        <section id="why" className="px-4 py-4 md:px-4 md:py-6">
          <div className="mx-auto max-w-6xl">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="mb-8 text-center md:mb-12"
            >
              <h2 className="text-cyan-200 text-2xl font-bold tracking-tight md:text-3xl">
                Why IDS?
              </h2>
              <p className="mt-2 text-sm text-zinc-200 md:text-base">
                Six reasons creators choose Inclusive Digital Sustenance
              </p>
            </motion.div>

            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
            >
              {whyCards.map((c) => (
                <motion.div
                  key={c.title}
                  variants={staggerItem}
                  className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-4 transition hover:border-teal-500/40 md:p-5"
                >
                  <div className="mb-3 text-2xl">{c.icon}</div>
                  <h3 className="text-sm font-semibold text-white">
                    {c.title}
                  </h3>
                  <p className="mt-1 text-xs text-zinc-200 md:text-sm">
                    {c.desc}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ───── 3. CAN YOU REALLY EARN? ───── */}
        <section id="earn" className="bg-zinc-900/40 px-4 py-4 md:px-4 md:py-6">
          <div className="mx-auto max-w-6xl">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="mb-8 text-center"
            >
              <h2 className="text-cyan-200 text-2xl font-bold md:text-3xl">
                Can You Really Earn?
              </h2>
              <p className="mt-2 text-sm text-zinc-200">
                Illustrative monthly earnings for Indian creators
              </p>
            </motion.div>

            {/* Mobile cards */}
            <div className="space-y-3 md:hidden">
              {earnings.map((e) => (
                <div
                  key={e.stage}
                  className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-4"
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                        e.badge === "teal"
                          ? "bg-teal-500/20 text-teal-300"
                          : e.badge === "amber"
                            ? "bg-amber-500/20 text-amber-300"
                            : "bg-zinc-700 text-zinc-200"
                      }`}
                    >
                      {e.stage}
                    </span>
                    <span className="text-sm font-semibold text-teal-400">
                      {e.range}
                    </span>
                  </div>
                  <p className="mt-1.5 text-xs text-zinc-200">{e.journey}</p>
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden overflow-hidden rounded-2xl border border-zinc-800 md:block">
              <table className="w-full text-left text-sm">
                <thead className="bg-zinc-900 text-zinc-200">
                  <tr>
                    <th className="px-5 py-3 font-medium">Stage</th>
                    <th className="px-5 py-3 font-medium">Journey</th>
                    <th className="px-5 py-3 font-medium">
                      Indicative Monthly Earnings
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {earnings.map((e) => (
                    <tr
                      key={e.stage}
                      className="border-t border-zinc-800 hover:bg-zinc-900/60"
                    >
                      <td className="px-5 py-3.5">
                        <span
                          className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                            e.badge === "teal"
                              ? "bg-teal-500/20 text-teal-300"
                              : e.badge === "amber"
                                ? "bg-amber-500/20 text-amber-300"
                                : "bg-zinc-700 text-zinc-200"
                          }`}
                        >
                          {e.stage}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-zinc-200">{e.journey}</td>
                      <td className="px-5 py-3.5 font-medium text-teal-400">
                        {e.range}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-center text-[11px] text-zinc-200 md:text-xs">
              Illustrative only. Actual income depends on audience, niche,
              geography, engagement and YouTube policies.
            </p>
          </div>
        </section>

        {/* ───── 4. YOUTUBE REVENUE INSIGHTS ───── */}
        <section className="px-4 py-4 md:px-4 md:py-6">
          <div className="mx-auto max-w-6xl">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="mb-8 text-center"
            >
              <h2 className="text-cyan-200 text-2xl font-bold md:text-3xl">
                YouTube Revenue Insights
              </h2>
              <p className="mt-2 text-sm text-zinc-200">
                Ads are only one piece. Build multiple income streams.
              </p>
            </motion.div>

            {/* Mobile cards */}
            <div className="space-y-3 md:hidden">
              {revenueSources.map((r) => (
                <div
                  key={r.source}
                  className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-4"
                >
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-sm font-semibold text-white">
                      {r.source}
                    </h3>
                    <span className="shrink-0 rounded-full bg-zinc-800 px-2 py-0.5 text-[10px] text-zinc-200">
                      {r.potential}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-zinc-200">{r.desc}</p>
                  <p className="mt-2 text-xs text-teal-400">{r.example}</p>
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden overflow-x-auto rounded-2xl border border-zinc-800 md:block">
              <table className="w-full text-left text-sm">
                <thead className="bg-zinc-900 text-zinc-200">
                  <tr>
                    <th className="px-5 py-3 font-medium">Revenue Source</th>
                    <th className="px-5 py-3 font-medium">Description</th>
                    <th className="px-5 py-3 font-medium">Potential</th>
                    <th className="px-5 py-3 font-medium">Example</th>
                  </tr>
                </thead>
                <tbody>
                  {revenueSources.map((r) => (
                    <tr
                      key={r.source}
                      className="border-t border-zinc-800 hover:bg-zinc-900/50"
                    >
                      <td className="px-5 py-3 font-medium">{r.source}</td>
                      <td className="px-5 py-3 text-zinc-200">{r.desc}</td>
                      <td className="px-5 py-3 text-zinc-200">{r.potential}</td>
                      <td className="px-5 py-3 text-teal-400">{r.example}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* ───── 5. WHAT CAN I CREATE? ───── */}
        <section
          id="create"
          className="bg-zinc-900/40 px-4 py-4 md:px-4 md:py-6"
        >
          <div className="mx-auto max-w-6xl">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="mb-8 text-center"
            >
              <h2 className="text-cyan-200 text-2xl font-bold md:text-3xl">
                What Can I Create?
              </h2>
              <p className="mt-2 text-sm text-zinc-200">
                Transformation categories that create lasting impact
              </p>
            </motion.div>

            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4"
            >
              {categories.map((c) => (
                <motion.div
                  key={c.title}
                  variants={staggerItem}
                  className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-3.5 transition hover:border-teal-500/30 md:p-4"
                >
                  <div className="text-xl md:text-2xl">{c.icon}</div>
                  <h3 className="mt-2 text-xs font-semibold text-white">
                    {c.title}
                  </h3>
                  <p className="mt-1 text-[11px] text-zinc-200 md:text-xs">
                    {c.desc}
                  </p>
                  <p className="mt-2 text-[10px] font-medium text-teal-500/80 md:text-[11px]">
                    {c.ideas}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ───── 6. FEATURED CREATORS ───── */}
        <section id="creators" className="px-4 py-4 md:px-4 md:py-6">
          <PurpleDoorIDSTable />
        </section>

        {/* ───── 7. HOW IDS HELPS YOU GROW ───── */}
        <section className="bg-zinc-900/40 px-4 py-4 md:px-4 md:py-6">
          <div className="mx-auto max-w-6xl">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="mb-8 text-center"
            >
              <h2 className="text-cyan-200 text-2xl font-bold md:text-3xl">
                How IDS Helps You Grow
              </h2>
              <p className="mt-2 text-sm text-zinc-200">
                Your journey from registration to recognition
              </p>
            </motion.div>

            <div className="flex flex-wrap items-center gap-2 md:gap-1.5">
              {journey.map((step, i) => (
                <div key={step} className="flex items-center gap-1.5">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.04 }}
                    className="rounded-xl border border-zinc-700 bg-zinc-900 px-3 py-2 text-center"
                  >
                    <div className="text-[9px] font-bold text-teal-500">
                      0{i + 1}
                    </div>
                    <div className="text-[11px] font-medium md:text-xs">
                      {step}
                    </div>
                  </motion.div>
                  {i < journey.length - 1 && (
                    <span className="hidden text-teal-600 md:inline">→</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ───── 8. WHY OUR MODEL IS DIFFERENT ───── */}
        <section className="px-4 py-4 md:px-4 md:py-6">
          <div className="mx-auto max-w-6xl">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="mb-8 text-center"
            >
              <h2 className="text-cyan-200 text-2xl font-bold md:text-3xl">
                Why Our Model Is Different
              </h2>
            </motion.div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5">
                <h3 className="mb-4 text-sm font-semibold text-zinc-100">
                  📺 Traditional YouTube
                </h3>
                <ul className="space-y-2 text-sm text-zinc-200">
                  {[
                    "Entertainment",
                    "Popularity",
                    "Views",
                    "Subscribers",
                    "Trends",
                    "Content",
                    "Virality",
                  ].map((t) => (
                    <li
                      key={t}
                      className="flex items-center gap-2 text-gray-100"
                    >
                      <span className="text-zinc-200">•</span> {t}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl border border-teal-500/30 bg-gradient-to-br from-teal-950/40 to-zinc-900/60 p-5">
                <h3 className="mb-4 text-sm font-semibold text-teal-300">
                  🌱 Inclusive Digital Sustenance
                </h3>
                <ul className="space-y-2 text-sm text-zinc-200">
                  {[
                    "Transformation",
                    "Purpose",
                    "Impact",
                    "Communities",
                    "Evergreen Knowledge",
                    "Digital Assets",
                    "Digital Legacy",
                  ].map((t) => (
                    <li
                      key={t}
                      className="flex items-center gap-2 text-gray-100"
                    >
                      <span className="text-teal-500">→</span> {t}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="#visionSimulation" className="px-4 py-4 md:px-4 md:py-6">
          <h1 className="text-cyan-200 text-xl lg:text-2xl">Think5Whys Content Creation Growth Vision Simulation</h1> 
        </section>
        <section id="creatorsDemo" className="px-4 py-4 md:px-4 md:py-6">
          <div className="mx-auto max-w-6xl">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="mb-6 text-center"
            >
              <h2 className="text-cyan-200 text-2xl font-bold md:text-3xl">
                Featured Transformation Creators (Future Vision Simulation)
              </h2>
              <p className="mt-2 text-sm text-zinc-200">
                Real people building meaningful channels
              </p>
            </motion.div>

            <input
              type="text"
              placeholder="Search by name, focus or language..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="mb-5 w-full rounded-full border border-zinc-700 bg-zinc-900 px-4 py-2.5 text-sm text-zinc-200 outline-none placeholder:text-zinc-200 focus:border-teal-500 md:max-w-md"
            />

            {/* Mobile cards */}
            <div className="space-y-3 md:hidden">
              {filteredCreators.map((c) => (
                <div
                  key={c.name}
                  className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900/50 p-3"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-teal-500 to-teal-700 text-xs font-bold text-white">
                    {c.initials}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold">
                      {c.name}
                    </div>
                    <div className="truncate text-[11px] text-zinc-200">
                      {c.focus} · {c.lang}
                    </div>
                    <div className="mt-0.5 text-[11px] text-zinc-200">
                      {c.subs} subs · ★ {c.rating}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden overflow-x-auto rounded-2xl border border-zinc-800 md:block">
              <table className="w-full text-left text-sm">
                <thead className="bg-zinc-900 text-zinc-200">
                  <tr>
                    <th className="px-4 py-3 font-medium">Creator</th>
                    <th className="px-4 py-3 font-medium">Focus</th>
                    <th className="px-4 py-3 font-medium">Language</th>
                    <th className="px-4 py-3 font-medium">Subs</th>
                    <th className="px-4 py-3 font-medium">Videos</th>
                    <th className="px-4 py-3 font-medium">Avg Views</th>
                    <th className="px-4 py-3 font-medium">Rating</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCreators.map((c) => (
                    <tr
                      key={c.name}
                      className="border-t border-zinc-800 hover:bg-zinc-900/50"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-teal-500 to-teal-700 text-[10px] font-bold text-white">
                            {c.initials}
                          </div>
                          <span className="font-medium">{c.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-zinc-200">{c.focus}</td>
                      <td className="px-4 py-3 text-zinc-200">{c.lang}</td>
                      <td className="px-4 py-3">{c.subs}</td>
                      <td className="px-4 py-3">{c.videos}</td>
                      <td className="px-4 py-3">{c.views}</td>
                      <td className="px-4 py-3 text-amber-400">★ {c.rating}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* ───── 9. DIGITAL LEGACY SCORE ───── */}
        <section className="bg-zinc-900/40 px-4 py-4 md:px-4 md:py-6">
          <div className="mx-auto max-w-6xl">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="mb-8 text-center"
            >
              <h2 className="text-cyan-200 text-2xl font-bold md:text-3xl">
                Digital Legacy Score (Future Vision Simulation)
              </h2>
              <p className="mt-2 text-sm text-zinc-200">
                A new metric that values meaning, impact and longevity
              </p>
            </motion.div>

            <div className="grid items-center gap-8 rounded-2xl border border-zinc-800 bg-zinc-900/50 p-6 md:grid-cols-[auto_1fr] md:p-8">
              <div className="flex flex-col items-center">
                <div className="relative flex h-36 w-36 items-center justify-center rounded-full bg-[conic-gradient(#14b8a6_0%_78%,#3f3f46_78%)] md:h-44 md:w-44">
                  <div className="absolute inset-3 rounded-full bg-zinc-900" />
                  <div className="relative text-center">
                    <div className="text-3xl font-bold text-teal-400 md:text-4xl">
                      78
                    </div>
                    <div className="text-xs text-zinc-200">/ 100</div>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap justify-center gap-1.5">
                  {["🌱 Impact Maker", "📚 Evergreen", "🤝 Community"].map(
                    (b) => (
                      <span
                        key={b}
                        className="rounded-full bg-zinc-800 px-2.5 py-0.5 text-[10px] text-zinc-200"
                      >
                        {b}
                      </span>
                    ),
                  )}
                </div>
              </div>

              <div className="space-y-3">
                {[
                  { label: "Meaningful Videos", val: 92 },
                  { label: "Community Impact", val: 85 },
                  { label: "Women Inspired", val: 78 },
                  { label: "Youth Benefited", val: 81 },
                  { label: "Businesses Helped", val: 70 },
                  { label: "Languages", val: 65 },
                  { label: "Community Rating", val: 88 },
                  { label: "Evergreen Content", val: 90 },
                  { label: "Consistency", val: 75 },
                ].map((f) => (
                  <div
                    key={f.label}
                    className="grid grid-cols-[110px_1fr_28px] items-center gap-2 text-xs md:grid-cols-[140px_1fr_32px] md:text-sm"
                  >
                    <span className="text-zinc-200">{f.label}</span>
                    <div className="h-1.5 overflow-hidden rounded-full bg-zinc-800">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${f.val}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                        className="h-full rounded-full bg-teal-500"
                      />
                    </div>
                    <span className="text-right text-zinc-200">{f.val}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ───── 10. LIVE IMPACT DASHBOARD ───── */}
        <section id="impact" className="px-4 py-4 md:px-4 md:py-6">
          <div className="mx-auto max-w-6xl">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="mb-8 text-center"
            >
              <h2 className="text-cyan-200 text-2xl font-bold md:text-3xl">
                Live Impact Dashboard (Future Vision Simulation)
              </h2>
              <p className="mt-2 text-sm text-zinc-200">
                Real progress from the IDS community
              </p>
            </motion.div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                { v: 4120, l: "Women Registered" },
                { v: 6890, l: "Youth Registered" },
                { v: 2840, l: "Creators" },
                { v: 12600, l: "Videos Published" },
                { v: 48, l: "Languages" },
                { v: 1.8, l: "Million+ Views", d: 1 },
                { v: 920, l: "MSMEs Benefited" },
                { v: 3400, l: "Jobs Enabled" },
              ].map((s) => (
                <motion.div
                  key={s.l}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-4 text-center md:p-5"
                >
                  <div className="text-xl font-bold text-teal-400 md:text-2xl">
                    <Counter value={s.v} decimals={s.d || 0} />
                  </div>
                  <div className="mt-1 text-[10px] text-zinc-200 md:text-xs">
                    {s.l}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ───── 11. FAQ ───── */}
        <section id="faq" className="bg-zinc-900/40 px-4 py-4 md:px-4 md:py-6">
          <div className="mx-auto max-w-2xl">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="mb-8 text-center"
            >
              <h2 className="text-cyan-200 text-2xl font-bold md:text-3xl">
                Frequently Asked Questions
              </h2>
            </motion.div>

            <div className="space-y-2">
              {faqs.map((f, i) => (
                <div
                  key={i}
                  className="overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900/50"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="flex w-full items-center justify-between px-4 py-3.5 text-left text-sm font-medium md:px-5"
                  >
                    {f.q}
                    <span
                      className="ml-3 text-zinc-200 transition-transform"
                      style={{
                        transform: openFaq === i ? "rotate(45deg)" : "none",
                      }}
                    >
                      +
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {openFaq === i && (
                      <motion.div
                        key="content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{
                          duration: 0.28,
                          ease: [0.22, 1, 0.36, 1],
                        }}
                        className="overflow-hidden"
                      >
                        <p className="px-4 pb-4 text-xs text-zinc-200 md:px-5 md:text-sm">
                          {f.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ───── 13. FOOTER ───── */}
        <footer className="border-t border-zinc-800 bg-zinc-950 px-4 py-12 md:px-6">
          <div className="mx-auto grid max-w-6xl gap-8 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <h3 className="text-base font-semibold text-white">Think5Whys</h3>
              <p className="mt-2 text-xs leading-relaxed text-zinc-200">
                Inclusive Digital Sustenance
                <br />
                Creating Digital Assets.
                <br />
                Enabling Digital Livelihoods.
                <br />
                Transforming Society.
              </p>
            </div>
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-zinc-200">
                Quick Links
              </h4>
              <ul className="mt-2 space-y-1.5 text-sm text-zinc-200">
                <li>
                  <a href="#why" className="hover:text-teal-400">
                    Why IDS
                  </a>
                </li>
                <li>
                  <a href="#earn" className="hover:text-teal-400">
                    Earnings
                  </a>
                </li>
                <li>
                  <a href="#create" className="hover:text-teal-400">
                    Categories
                  </a>
                </li>
                <li>
                  <a href="#creators" className="hover:text-teal-400">
                    Creators
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-zinc-200">
                Resources
              </h4>
              <ul className="mt-2 space-y-1.5 text-sm text-zinc-200">
                <li>
                  <a href="#" className="hover:text-teal-400">
                    Creator Guide
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-teal-400">
                    Demo Video
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-teal-400">
                    Community
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-zinc-200">
                Contact
              </h4>
              <ul className="mt-2 space-y-1.5 text-sm text-zinc-200">
                <li>
                  <a
                    href="mailto:contact@think5whys.com"
                    className="hover:text-teal-400"
                  >
                    contact@think5whys.com
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-teal-400">
                    Twitter / X
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-teal-400">
                    LinkedIn
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="mx-auto mt-5 flex max-w-6xl flex-col items-center justify-between gap-2 border-t border-zinc-800 pt-6 text-xs text-zinc-200 sm:flex-row">
            <span>© 2026 Think5Whys. All rights reserved.</span>
            <span className="italic">
              Creating Digital Assets. Enabling Digital Livelihoods.
              Transforming Society.
            </span>
          </div>
        </footer>
      </div>
    </MotionConfig>
  );
}
